<template>
  <div class="sda-container">
    
    <portal to="toolbar-title">
      Νέο Δελτίο Αποστολής
    </portal>
    <portal to="toolbar-items" v-if="sdaData && sdaData.SDAInv">
      <v-btn outlined small @click="showSDAInvDialog = true">
        <v-icon>mdi-qrcode</v-icon>  Συγκ. ΔΑ
      </v-btn>
    </portal>

    <v-container fluid :class="{'pb-16': hasSelectedItems && step === 1}">
      <!-- Alerts Section -->
      <SDAAlerts 
        :sda-data="sdaData" 
        :error="error"
        @refresh-sda="openConfirmDialog('refresh')"
        @close-sda="openConfirmDialog('close')"
      />

      <!-- Step 1: All items -->
      <template v-if="step === 1">
        <!-- Client Info -->
        <SDAClientInfo 
          :sda-data="sdaData" 
          :only-show-s-d-a-inv="onlyShowSDAInv" 
        />

        <!-- Available Products -->
        <SDAProductList
          :sda-data="sdaData"
          :search="search"
          :selected-items="selectedItems"
          :is-s-d-a-expired="isSDAExpired"
          :only-show-s-d-a-inv="onlyShowSDAInv"
          @search-change="search = $event"
          @info-click="openDialog"
          @increment="incrementItem"
          @decrement="decrementItem"
          @quantity-change="handleQuantityChange"
        />

        <!-- Exhausted Products -->
        <SDAExhaustedProducts
          :sda-data="sdaData"
          :search="search"
          :only-show-s-d-a-inv="onlyShowSDAInv"
          @info-click="openDialog"
        />
      </template>

      <!-- Step 2: Only selected items -->
      <template v-else-if="step === 2">
        <SDASelectedProducts
          :selected-s-d-a-items="selectedSDAItems"
          :selected-items="selectedItems"
          :notes="notes"
          @go-to-step="goToStep"
          @info-click="openDialog"
          @increment="incrementItem"
          @decrement="decrementItem"
          @quantity-change="handleQuantityChange"
          @notes-change="notes = $event"
        />
      </template>

      <!-- Step 3: Success message -->
      <template v-else-if="step === 3">
        <SDASuccessResult
          :last-s-d-a-result="lastSDAResult"
          @close="close"
        />
      </template>

      <!-- Loading State -->
      <v-row v-if="!sdaData && !error">
        <v-col cols="12">
          <v-skeleton-loader
            type="card, list-item, list-item, list-item, list-item"
            class="mx-auto"
            max-width="600"
            height="300"
          />
        </v-col>
      </v-row>
    </v-container>

    <!-- Related Deliveries Dialog -->
    <SDARelatedDeliveries
      :show.sync="dialog"
      :current-relate-d-a="currentRelateDA"
    />

    <!-- SDA Invoice Dialog -->
    <SDAInvDialog
      :show.sync="showSDAInvDialog"
      :sda-data="sdaData"
      :is-s-d-a-expired="isSDAExpired"
      @refresh-sda="openConfirmDialog('refresh')"
      @close-sda="openConfirmDialog('close')"
    />

    <!-- Confirmation and Result Dialogs -->
    <SDADialogs
      :show-confirm-dialog.sync="showConfirmDialog"
      :show-result-dialog.sync="showResultDialog"
      :confirm-action="confirmAction"
      :result-message="resultMessage"
      :result-type="resultType"
      :loading="loading"
      @confirm-action="confirmAndRunAction"
      @reset-result-dialog="resetResultDialog"
    />

    <!-- Bottom Bar -->
    <SDABottomBar
      :step="step"
      :total-selected-items="totalSelectedItems"
      :has-selected-items="hasSelectedItems"
      @next-step="goToStep(2)"
      @save-selected="saveSelected"
    />

    <!-- Loading Overlay -->
    <v-overlay :value="loading" absolute opacity="0.7" z-index="10000">
      <v-progress-circular indeterminate size="64" color="primary" />
    </v-overlay>
  </div>
</template>

<script>
import api from '@/wl-core/api'
import moment from 'moment'

// Import components
import SDAAlerts from './components/SDAAlerts.vue'
import SDAClientInfo from './components/SDAClientInfo.vue'
import SDAProductList from './components/SDAProductList.vue'
import SDAExhaustedProducts from './components/SDAExhaustedProducts.vue'
import SDASelectedProducts from './components/SDASelectedProducts.vue'
import SDASuccessResult from './components/SDASuccessResult.vue'
import SDARelatedDeliveries from './components/SDARelatedDeliveries.vue'
import SDAInvDialog from './components/SDAInvDialog.vue'
import SDADialogs from './components/SDADialogs.vue'
import SDABottomBar from './components/SDABottomBar.vue'

export default {
  name: 'SDA',
  components: {
    SDAAlerts,
    SDAClientInfo,
    SDAProductList,
    SDAExhaustedProducts,
    SDASelectedProducts,
    SDASuccessResult,
    SDARelatedDeliveries,
    SDAInvDialog,
    SDADialogs,
    SDABottomBar
  },
  data() {
    return {
      sdaData: null,
      error: null,
      search: '',
      selectedItems: {},
      showInfo: true,
      step: 1,
      dialog: false,
      notes: '',
      currentRelateDA: null,
      showSDAInvDialog: false,
      loading: false,
      lastSDAResult: null,
      confirmAction: null,
      resultMessage: '',
      resultType: null,
      showConfirmDialog: false,
      showResultDialog: false,
      onlyShowSDAInv: false,
    }
  },
  computed: {
    totalSelectedItems() {
      return Object.values(this.selectedItems).reduce((sum, value) => sum + (value || 0), 0)
    },
    hasSelectedItems() {
      return this.totalSelectedItems > 0
    },
    selectedSDAItems() {
      if (!this.sdaData?.SDAInv?.SDAItems) return []
      return this.sdaData.SDAInv.SDAItems.filter(item => this.selectedItems[item.PartnerMvItm_ID] > 0)
    },
    isSDAExpired() {
      if (!this.sdaData?.SDAInv?.HmeromExpire) return false;
      return moment().isAfter(moment(this.sdaData.SDAInv.HmeromExpire));
    }
  },
  methods: {
    goToStep(step) {
      this.step = step
    },
    async saveSelected() {
      this.loading = true
      try {
        // Prepare items array as required for postSDA
        const items = this.selectedSDAItems.map(item => ({
          item,
          value: this.selectedItems[item.PartnerMvItm_ID]
        }))
        // Call the API
        const result = await api.sda.postSDA(this.sdaData.client, items, this.notes)
        // Store the whole result for step 3 (including Errors)
        this.lastSDAResult = result
        this.step = 3
      } catch (err) {
        this.error = err.message || 'Αποτυχία αποθήκευσης ΔΑ'
        this.step = 3
      } finally {
        this.loading = false
      }
    },
    incrementItem(item) {
      const currentValue = this.selectedItems[item.PartnerMvItm_ID] || 0
      if (currentValue < item.AvailPosot) {
        this.selectedItems[item.PartnerMvItm_ID] = currentValue + 1
        this.updateSelectedItem(item)
      }
    },
    decrementItem(item) {
      const currentValue = this.selectedItems[item.PartnerMvItm_ID] || 0
      if (currentValue > 0) {
        this.selectedItems[item.PartnerMvItm_ID] = currentValue - 1
        this.updateSelectedItem(item)
      }
    },
    handleQuantityChange(item, value) {
      this.selectedItems[item.PartnerMvItm_ID] = Number(value)
      this.updateSelectedItem(item)
    },
    updateSelectedItem(item) {
      let value = this.selectedItems[item.PartnerMvItm_ID]
      value = Number(value)
      if (isNaN(value) || value < 0) {
        value = 0
      } else if (value > item.AvailPosot) {
        value = item.AvailPosot
      }
      this.selectedItems[item.PartnerMvItm_ID] = value
      this.$forceUpdate()
    },
    openDialog(item) {
      this.currentRelateDA = item.RelateDA.map(da => ({ ...da, expanded: false }))
      this.dialog = true
    },
    close() {
      document.title = 'Close'
    },
    handleBackButton(event) {
      if (this.step === 2) {
        this.goToStep(1)
        history.pushState(null, null, location.href) // Prevent actual back
      } else if (this.step !== 1 && this.step !== 3) {
        if (!window.confirm('Ειστε σιγουρος;')) {
          history.pushState(null, null, location.href)
        }
      }
    },
    openConfirmDialog(action) {
      this.confirmAction = action;
      this.showConfirmDialog = true;
    },
    async confirmAndRunAction() {
      this.loading = true;
      try {
        const doRefresh = this.confirmAction === 'refresh';
        const result = await api.sda.closeSDA(doRefresh);
        this.resultMessage = 'Η ενέργεια ολοκληρώθηκε με επιτυχία.';
        this.resultType = 'success';
      } catch (err) {
        this.resultMessage = 'Παρουσιάστηκε σφάλμα. Παρακαλώ δοκιμάστε ξανά ή επικοινωνήστε με το γραφείο.';
        this.resultType = 'error';
      } finally {
        this.loading = false;
        this.showConfirmDialog = false;
        this.showResultDialog = true;
        this.confirmAction = null;
        this.sdaData = null;
        await this.init();
        this.step = 1;
        //timeout close the dialogs after 5 seconds
        setTimeout(() => {
          this.showConfirmDialog = false;
          this.showResultDialog = false;
          this.showSDAInvDialog = false;
        }, 5000);
      }
    },
    resetResultDialog() {
      this.resultMessage = '';
      this.resultType = null;
      this.showResultDialog = false;
    },
    async init() {
      // Reset state
      this.selectedItems = {};
      this.error = null;
      this.step = 1;
      this.dialog = false;
      this.currentRelateDA = null;
      this.showSDAInvDialog = false;
      this.loading = true;
      this.lastSDAResult = null;
      this.confirmAction = null;
      this.showConfirmDialog = false;
      try {
        const { Partner_ID, Branch_ID, Project_ID } = this.$route.params;
        this.sdaData = await api.sda.getSDA(
          parseInt(Partner_ID),
          parseInt(Branch_ID),
          parseInt(Project_ID)
        );
        if(Partner_ID === '0'){
          this.onlyShowSDAInv = true;
        }
        this.sdaData.SDAInv.SDAItems.forEach(item => {
          this.$set(this.selectedItems, item.PartnerMvItm_ID, 0);
        });
        // Add popstate listener for back button
        window.addEventListener('popstate', this.handleBackButton);
      } catch (err) {
        this.error = err.message || 'Failed to fetch SDA data';
      } finally {
        this.loading = false;
      }
    },
  },
  watch: {
    step(newStep) {
      if (newStep === 2 && this.selectedSDAItems.length === 0) {
        this.step = 1
      }
    },
    selectedSDAItems(newVal) {
      if (this.step === 2 && newVal.length === 0) {
        this.step = 1
      }
    }
  },
  async created() {
    await this.init();
  },
  beforeDestroy() {
    window.removeEventListener('popstate', this.handleBackButton)
  }
}
</script>

<style scoped>
.sda-container {
  /* padding: 20px; */
}
</style> 